/* eslint-disable @typescript-eslint/no-empty-interface */
import * as React from 'react';
import { atom, useAtomValue } from 'jotai';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'next-i18next';

import { Worker } from '@restworld/utility-types';

import {
  EditModalFooter,
  EditModalTitle,
  EditFieldOptionsModal,
  IEditModalProps,
  FieldOptionsViewType,
} from '../profile-edit-modal';
import { getWorkerAtom, getWorkerFieldOptionsAtom } from '../../../global-state.worker-data';
import { useApiSubmitHandler } from '../../hooks';
import { CheckboxGroup, FormLabelWrapper, HelperTextRElement, TextInput } from '@restworld/ui-ds-base';

interface ITransportationFormContentProps extends IEditModalProps {}
interface InputProps {
  driving_licenses: string[];
  transportation_options: string[];
  displacement_availability: string;
  displacement_preferences: string[];
}
const defaultValuesAtom = atom((get) => {
  const { data: workerData } = get(getWorkerAtom);
  return {
    driving_licenses: workerData?.driving_licenses?.map((v) => v.driving_license_var.id) ?? [],
    transportation_options: workerData?.transportation_options.map((v) => v.transportation_var.id) ?? [],
    displacement_availability: workerData?.displacement_availability ?? '',
    displacement_preferences: workerData?.displacement_preferences?.map((v) => v.displacement_preference_var.id) ?? [],
  };
});
// -- main -- //
export const TransportationFormContent: React.FunctionComponent<ITransportationFormContentProps> = ({
  editPageTitle,
  onClose,
}) => {
  const { t } = useTranslation();
  const { fieldOptionsView, toggleFieldOptionsView, resetFieldOptionsView } = useFieldOptions();
  const defaultValues: InputProps = useAtomValue(defaultValuesAtom);
  const { data: workerFieldOptionsData } = useAtomValue(getWorkerFieldOptionsAtom);
  const {
    handleSubmit,
    formState: { errors },
    control,
    getValues,
    setValue,
    reset,
    watch,
  } = useForm<InputProps>({ reValidateMode: 'onSubmit', defaultValues });
  const { apiSubmitHandler, loading } = useApiSubmitHandler({ onClose, editPageType: 'transportation' });
  const onSubmit: SubmitHandler<InputProps> = React.useCallback(
    async (data) => {
      try {
        const params: Partial<Worker> = {
          ...data,
          driving_licenses: data.driving_licenses?.map((licenseId) => {
            const drivingLicense = workerFieldOptionsData?.['drivingLicenseOptions']?.find(
              (v: { id: string }) => v.id === licenseId
            );
            return {
              driving_license_var: {
                id: licenseId,
                label: drivingLicense?.label,
                key: drivingLicense?.key,
              },
            };
          }),
          displacement_preferences: data.displacement_preferences?.map((displacementId) => {
            const displacementPreference = workerFieldOptionsData?.['displacementPreference']?.find(
              (v: { id: string }) => v.id === displacementId
            );
            return {
              displacement_preference_var: {
                id: displacementId,
                label: displacementPreference?.label,
                key: displacementPreference?.key,
              },
            };
          }),
          transportation_options: data.transportation_options?.map((transportationId) => {
            const transportationOption = workerFieldOptionsData?.['transportationOptions']?.find(
              (v: { id: string }) => v.id === transportationId
            );
            return {
              transportation_var: {
                id: transportationId,
                label: transportationOption?.label,
                key: transportationOption?.key,
              },
            };
          }),
        };
        apiSubmitHandler(params);
      } catch (error) {
        console.error(error, `error in transportation submit`);
      }
    },
    [apiSubmitHandler, workerFieldOptionsData]
  );
  const drivingLicenseEl = React.useMemo(
    () => (
      <FormLabelWrapper id="driving_licenses" label={t('onboarding-page:what_licenses_do_you_have')}>
        <Controller
          key="driving_licenses"
          name="driving_licenses"
          rules={{ required: false }}
          control={control}
          render={({ field }) => {
            return (
              <>
                <CheckboxGroup
                  key="driving_licenses"
                  id="driving_licenses"
                  options={
                    workerFieldOptionsData?.['drivingLicenseOptions']
                      ? [...workerFieldOptionsData['drivingLicenseOptions']]
                      : []
                  }
                  mode="clickable"
                  variant="box"
                  isWrapList={false}
                  isOrientation="row"
                  {...{
                    ...field,
                    onChange: (e) => {
                      let checkV: string[] = field.value ? [...field.value] : [];
                      checkV =
                        !checkV.includes(e.target.id) && e.target.checked
                          ? [...checkV, e.target.id]
                          : checkV.filter((v) => v !== e.target.id);
                      field.onChange(checkV.length ? checkV : []);
                    },
                  }}
                />
              </>
            );
          }}
        />
        {Object.keys(errors).includes('driving_licenses') ? (
          <HelperTextRElement helperText={t('onboarding-page:this_field_is_required')} />
        ) : null}
      </FormLabelWrapper>
    ),
    [control, errors, workerFieldOptionsData, t]
  );
  const transportationOptionsEl = React.useMemo(
    () => (
      <FormLabelWrapper
        isFullWidth
        id="transportation_options"
        label={t('onboarding-page:what_means_of_transportation_do_you_use_to_get_to_work')}
      >
        <Controller
          name="transportation_options"
          control={control}
          rules={{ required: false }}
          render={({ field }) => (
            <TextInput
              id="transportation_options"
              placeholder={t('onboarding-page:select_one_or_more_options')}
              type="text"
              rightIcon={ChevronDownIcon}
              helperText={
                Object.keys(errors).includes('transportation_options') ? (
                  <HelperTextRElement helperText={t('onboarding-page:this_field_is_required')} />
                ) : null
              }
              color={Object.keys(errors).includes('transportation_options') ? 'failure' : undefined}
              readOnly
              {...field}
              value={
                field.value
                  .map(
                    (v) =>
                      workerFieldOptionsData?.['transportationOptions']?.find((d: { id: string }) => d.id === v)?.label
                  )
                  .join(', ') || ''
              }
              onClick={() =>
                toggleFieldOptionsView((p) => ({
                  ...p,
                  showOptionsView: true,
                  options: workerFieldOptionsData?.['transportationOptions'],
                  mainTitle: t('onboarding-page:what_means_of_transportation_do_you_use_to_get_to_work'),
                  id: 'transportation_options',
                  selected: field.value,
                  returnType: 'arrayString',
                }))
              }
            />
          )}
        />
      </FormLabelWrapper>
    ),
    [control, errors, toggleFieldOptionsView, workerFieldOptionsData, t]
  );
  const displacementAvailabilityEl = React.useMemo(
    () => (
      <FormLabelWrapper
        isFullWidth
        id="displacement_availability"
        label={t('onboarding-page:are_you_available_to_move')}
      >
        <Controller
          name="displacement_availability"
          control={control}
          rules={{ required: false }}
          render={({ field }) => (
            <TextInput
              id="displacement_availability"
              placeholder={t('common:select_an_answer')}
              type="text"
              rightIcon={ChevronDownIcon}
              helperText={
                Object.keys(errors).includes('displacement_availability') ? (
                  <HelperTextRElement helperText={t('onboarding-page:this_field_is_required')} />
                ) : null
              }
              color={Object.keys(errors).includes('displacement_availability') ? 'failure' : undefined}
              readOnly
              {...field}
              value={
                workerFieldOptionsData?.['displacementAvailability']?.find((v) => v.id === field.value)?.label || ''
              }
              onClick={() =>
                toggleFieldOptionsView((p) => ({
                  ...p,
                  showOptionsView: true,
                  options: workerFieldOptionsData?.['displacementAvailability'],
                  mainTitle: t('onboarding-page:are_you_available_to_move'),
                  id: 'displacement_availability',
                  selected: field.value,
                  returnType: 'string',
                }))
              }
            />
          )}
        />
      </FormLabelWrapper>
    ),
    [control, errors, toggleFieldOptionsView, workerFieldOptionsData, t]
  );
  const displacementPreferencesEl = React.useMemo(
    () => (
      <FormLabelWrapper isFullWidth id="displacement_preferences" label={t('onboarding-page:if_yes_where')}>
        <Controller
          name="displacement_preferences"
          control={control}
          rules={{ required: false }}
          render={({ field }) => (
            <TextInput
              id="displacement_preferences"
              placeholder={t('common:select_an_answer')}
              type="text"
              rightIcon={ChevronDownIcon}
              helperText={
                Object.keys(errors).includes('displacement_preferences') ? (
                  <HelperTextRElement helperText={t('onboarding-page:this_field_is_required')} />
                ) : null
              }
              color={Object.keys(errors).includes('displacement_preferences') ? 'failure' : undefined}
              readOnly
              {...field}
              value={
                field.value
                  .map(
                    (v) =>
                      workerFieldOptionsData?.['displacementPreference']?.find((d: { id: string }) => d.id === v)?.label
                  )
                  .join(', ') || ''
              }
              disabled={
                workerFieldOptionsData?.['displacementAvailability']?.find(
                  (v) => v.id === watch('displacement_availability')
                )?.key === 'displacement_availability_no'
              }
              onClick={() =>
                toggleFieldOptionsView((p) => ({
                  ...p,
                  showOptionsView: true,
                  options: workerFieldOptionsData?.['displacementPreference'],
                  mainTitle: t('onboarding-page:where_would_you_like_to_move'),
                  id: 'displacement_preferences',
                  selected: field.value,
                  returnType: 'arrayString',
                }))
              }
            />
          )}
        />
      </FormLabelWrapper>
    ),
    [control, errors, toggleFieldOptionsView, watch, workerFieldOptionsData, t]
  );

  const handleChange = React.useCallback(
    (value: string | React.ChangeEvent<HTMLInputElement>) => {
      if (typeof value === 'string') {
        toggleFieldOptionsView((p) => ({ ...p, selected: value }));
      } else {
        const values = fieldOptionsView.selected ?? [];
        let checkV: string[] = values ? [...values] : [];
        checkV =
          !checkV.includes(value.target.id) && value.target.checked
            ? [...checkV, value.target.id]
            : checkV.filter((v) => v !== value.target.id);
        toggleFieldOptionsView((p) => ({ ...p, selected: checkV }));
      }
    },
    [fieldOptionsView.selected, toggleFieldOptionsView]
  );

  const handleSave = React.useCallback(() => {
    if (
      fieldOptionsView.id === 'displacement_availability' &&
      fieldOptionsView.options.find((v) => v.id === fieldOptionsView.selected)?.key === 'displacement_availability_no'
    )
      setValue('displacement_preferences', []);
    setValue(fieldOptionsView.id, fieldOptionsView.selected);
    resetFieldOptionsView();
  }, [fieldOptionsView.id, fieldOptionsView.options, fieldOptionsView.selected, resetFieldOptionsView, setValue]);

  return (
    <>
      {/* return !fieldOptionsView.showOptionsView ? ( */}
      <div className="w-full /* p-6 bg-primary-50 */">
        <EditModalTitle editPageTitle={editPageTitle} onClose={onClose} />
        <form autoComplete="off" method="post" onSubmit={handleSubmit(onSubmit)} className="flex gap-4 flex-col">
          <div className="flex flex-col md:flex-row gap-4">
            {drivingLicenseEl}
            {transportationOptionsEl}
          </div>
          {displacementAvailabilityEl}
          {displacementPreferencesEl}
          <EditModalFooter
            loading={loading}
            onClose={() => {
              reset(defaultValues);
              onClose();
            }}
            editPageType="transportation"
          />
        </form>
      </div>
      {/* ) : ( */}
      <EditFieldOptionsModal
        {...fieldOptionsView}
        mainTitle={fieldOptionsView.mainTitle}
        value={fieldOptionsView.selected ?? getValues(fieldOptionsView.id)}
        onChange={handleChange}
        onBack={() => {
          resetFieldOptionsView();
        }}
        onSave={handleSave}
        visible={fieldOptionsView.showOptionsView}
      />
      {/* ); */}
    </>
  );
};

export default TransportationFormContent;

interface FieldOptionsModalViewType extends Omit<FieldOptionsViewType, 'id'> {
  id: 'transportation_options' | 'displacement_availability' | 'displacement_preferences';
}

const useFieldOptions = () => {
  const defaultFieldOptions: FieldOptionsModalViewType = React.useMemo(
    () => ({
      showOptionsView: false,
      options: [],
      mainTitle: '',
      subTitle: '',
      id: 'transportation_options',
      returnType: 'string',
    }),
    []
  );
  const defaultModalFieldOptions = React.useMemo(
    () => ({ ...defaultFieldOptions, id: 'transportation_options' } as FieldOptionsModalViewType),
    [defaultFieldOptions]
  );
  const [fieldOptionsView, toggleFieldOptionsView] = React.useState<FieldOptionsModalViewType | null>({
    ...defaultModalFieldOptions,
  });
  const resetFieldOptionsView = React.useCallback(() => {
    toggleFieldOptionsView(defaultModalFieldOptions);
  }, [defaultModalFieldOptions]);
  return {
    fieldOptionsView,
    toggleFieldOptionsView,
    resetFieldOptionsView,
  };
};
